import { eq } from '@art-suite/art-core-ts';
import { expressionMapValueIsEquation, expressionMapValueIsNumberOrUndefined, getAllParseAssetsForExpressions, getExpressionMapNumbers } from './InternalLib';
import { ParsedExpression } from './InternalTypes';
import { OptimizedValueExpressionsBase } from './OptimizedValueExpressionsBase';
import { ExpressionError, ExpressionMap, Scope, SetValues } from './OptimizedValueExpressionTypes';
import { standardFunctions } from './StandardFunctions';

export class OptimizedValueExpressions implements OptimizedValueExpressionsBase {
  private readonly _resolved: Record<string, number | undefined> = {};
  private readonly _calculatedValues: Record<string, number | undefined> = {};
  private readonly _expressions: Record<string, string> = {};
  private readonly _parsedExpressionsByName: Record<string, ParsedExpression> = {};
  private readonly _orderedParsedExpressions: ParsedExpression[] = [];
  private _dependencyGraph: Record<string, string[]> = {};
  readonly errors: ExpressionError[] = [];
  definedValues: string[] = [];
  evaluationOrder: string[] = [];

  constructor(
    readonly expressionMap: ExpressionMap,
    readonly scope: Scope,
    readonly setValues: SetValues = {}, // setValues override values
    _basedOn?: OptimizedValueExpressions, // for internal use only; call withNewScope instead; this is purely an efficiency optimization
  ) {
    this._calculatedValues = { ...this.scope, ...getExpressionMapNumbers(this.expressionMap) };
    this._resolved = { ...this._calculatedValues, ...this.setValues };

    if (_basedOn) { // this is purely an efficiency optimization - _basedOn could be omitted with no logical difference in how the class works
      // validate all keys in _basedOn.scope are in this.scope
      for (const key in _basedOn.scope) {
        if (!(key in this.scope)) this.errors.push({ name: key, error: `Key "${key}" not found in new scope` });
      }
      this._expressions = _basedOn._expressions;
      this._parsedExpressionsByName = _basedOn._parsedExpressionsByName;
      this._orderedParsedExpressions = _basedOn._orderedParsedExpressions;
      this.definedValues = _basedOn.definedValues;
      this.evaluationOrder = _basedOn.evaluationOrder;
      this.errors.push(..._basedOn.errors);
    } else {
      this.initExpressionsFromExpressionMap();
      const allParseAssets = getAllParseAssetsForExpressions(this._expressions);
      this._parsedExpressionsByName = allParseAssets.parsedExpressionsByName;
      this._dependencyGraph = allParseAssets.dependencyGraph;
      this.evaluationOrder = allParseAssets.evaluationOrder;
      this._orderedParsedExpressions = allParseAssets.orderedParsedExpressions;
      if (allParseAssets.errors) this.errors.push(...allParseAssets.errors);
    }

    if (this.errors.length > 0) return;
    this.resolveExpressions();
  }

  eq(other: OptimizedValueExpressions) {
    return eq(this._resolved, other._resolved); // should be the same if the 3 above are the same, but since this is the primary result, we check it explicitly
  }

  validate() {
    if (this.errors.length > 0) throw new Error(this.errors.map(error => error.error).join('\n'));
  }

  // @art-suite/art-core-ts's log function uses this for better logging; should return any JSON serializable object
  getPlainObjects() {
    return {
      class: 'OptimizedValueExpressions',
      resolved: this._resolved,
      calculatedValues: this._calculatedValues,
      expressions: this._expressions,
      definedValues: this.definedValues,
      dependencyGraph: this._dependencyGraph,
      expressionOrder: this.evaluationOrder,
      errors: this.errors,
    };
  }

  get missingDependenciesErrors() {
    const missingDependencies: ExpressionError[] = [];
    for (const parsedExpression of this._orderedParsedExpressions) {
      for (const dependency of parsedExpression.dependencies) {
        if (!this.definedValues.includes(dependency)) {
          missingDependencies.push({
            name: dependency,
            error:
              `No calculatable values named "${dependency}" found. Value name: "${parsedExpression.name}". Equation: "${parsedExpression.expression}". Named values found: ${
                this.definedValues.join(', ')
              }`,
          });
        }
      }
    }
    return missingDependencies;
  }

  validateAllDependenciesDefined(context?: string) {
    this.validate();
    const { missingDependenciesErrors } = this;
    if (missingDependenciesErrors.length > 0) {
      throw new Error((context ? `${context}:\n` : '') + missingDependenciesErrors.map(error => error.error).join('\n'));
    }
  }

  get resolved() {
    this.validate();
    return this._resolved;
  }

  get calculatedValues() {
    this.validate();
    return this._calculatedValues;
  }

  // *************************************************************************
  // PUBLIC METHODS
  // *************************************************************************

  /**
   * Returns a new OptimizedValueExpressions instance with the same expressions but with new values for the scope. Avoids re-validating, parsing and dependency-resolving the expressions.
   * @param scope - The new scope to use - must be the same keys as the original scope only with new values
   * @param setValues - The new set values to use - will override the values in the new scope
   * @returns A new OptimizedValueExpressions instance with the same expressions but a new scope
   */
  withNewScope(scope: Scope, setValues?: SetValues) {
    this.validate();
    setValues = setValues ?? this.setValues;
    if (eq(this.scope, scope) && eq(this.setValues, setValues)) { // no change, return the same instance
      return this;
    } else {
      return new OptimizedValueExpressions(this.expressionMap, scope, setValues, this);
    }
  }

  /**
   * Returns a new OptimizedValueExpressions instance with the same expressions but with new set values. Avoids re-validating, parsing and dependency-resolving the expressions.
   * @param setValues - The new set values to use - selectively override this.values
   * @returns A new OptimizedValueExpressions instance with the same expressions but a new set of values
   */
  // withNewSetValues(setValues: SetValues) {
  //   this.validate();
  //   return new OptimizedValueExpressions(this.expressionMap, this.scope, setValues, this);
  // }

  // *************************************************************************
  // PRIVATE METHODS
  // *************************************************************************
  private resolveExpressions() {
    for (const parsedExpression of this._orderedParsedExpressions) {
      // dependencies resolve through effective values: an overridden dependency contributes its setValue
      const dependencies = parsedExpression.dependencies;
      let haveAllDependencies = true;
      for (const dependency of dependencies) {
        if (this._resolved[dependency] == null) {
          haveAllDependencies = false;
          break;
        }
      }
      const calculatedValue = haveAllDependencies
        ? parsedExpression.parsedExpression.evaluate(this._resolved as any, standardFunctions)
        : undefined;
      this._calculatedValues[parsedExpression.name] = calculatedValue;
      if (this._resolved[parsedExpression.name] === undefined) {
        this._resolved[parsedExpression.name] = calculatedValue;
      }
    }
  }

  private initExpressionsFromExpressionMap() {
    for (const key in this.expressionMap) {
      const value = this.expressionMap[key];
      if (expressionMapValueIsEquation(value)) {
        this._expressions[key] = value;
      } else if (!expressionMapValueIsNumberOrUndefined(value)) this.errors.push({ name: key, error: `Unsupported type for key "${key}": ${value}` });
    }
    this.definedValues = [];
    for (const key in this._resolved) this.definedValues.push(key);
    for (const key in this._expressions) this.definedValues.push(key);
  }
}
